import Button from "@components/Utils/Button";
import EmailVerificationCta from "@components/Utils/EmailVerificationCta";
import InstallAddonCta from "@components/Utils/InstallAddonCta";
import {
  selectAccountData,
  selectAccountDataActions,
  selectOldStripeData,
} from "@store/slices/accountSlice";
import { selectArchiveDataActions } from "@store/slices/archiveSlice";
import useStore from "@store/store";
import axios from "axios";
import React, { DOMAttributes, useEffect, useMemo, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useUser } from "../../utils/AuthContext";
import { VBA } from "../../utils/types";

import { v4 as uuidv4 } from "uuid";
import ResubscribeCta from "@components/Utils/ResubscribeCta";
import { EXCEL_BOT_TOKEN } from "../../utils/constants";

const VbaBuilder = () => {
  const { user } = useUser();
  const accountData: any = useStore(selectAccountData);
  const oldStripeData: any = useStore(selectOldStripeData);
  const { addVba, updateVbaFeedback } = useStore(selectArchiveDataActions);
  const { setCreditBalance } = useStore(selectAccountDataActions);

  const [currentVba, setCurrentVba] = useState<VBA>();
  const [generate, setGenerate] = useState<boolean>(true);
  const [explain, setExplain] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [loadingFeedback, setLoadingFeedback] = useState<boolean>(false);
  const [feedbackRegistered, setFeedbackRegistered] = useState<boolean>(false);
  const [response, setResponse] = useState("");

  const placeholder = useMemo(() => {
    if (explain)
      return `Sub Last_Column()
      Dim LC As Long
      LC = Cells(1, Columns.Count).End(xlToLeft).Column
      MsgBox LC
End Sub
`;

    if (generate) return "Find the last used column in the current sheet";
  }, [generate, explain]);

  const output = useMemo(() => {
    if (generate)
      return `Sub Last_Column()
      Dim LC As Long
      LC = Cells(1, Columns.Count).End(xlToLeft).Column
      MsgBox LC
End Sub
`;

    if (explain)
      return `1. The first line of code declares a variable called LC as a Long data type.
2. The second line of code sets the value of LC to the last column in the worksheet. This is done by using the Cells() function to select the cell in the first row and the last column of the worksheet, then using the End() function to move to the last column.
3. The third line of code displays a message box with the value of LC, which is the last column in the worksheet.`;
  }, [generate, explain]);

  useEffect(() => {
    let intervalId: NodeJS.Timer;
    if (copied) {
      intervalId = setInterval(() => {
        setCopied(false);
      }, 1000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [copied]);

  const handleFeedback = async (isCorrect: boolean) => {
    setLoadingFeedback(true);
    const res = await axios.post(
      "/api/feedback",
      {
        uid: accountData?.uid,
        id: currentVba?.id,
        operation: currentVba?.operation,
        type: "VBA",
        isCorrect,
      },
      {
        headers: {
          // @ts-ignore
          Authorization: `Bearer ${user.accessToken}`,
          ExcelBotToken: EXCEL_BOT_TOKEN,
        },
      }
    );

    if (res.status === 200) {
      updateVbaFeedback(currentVba?.id as string, isCorrect);
      setCurrentVba({ ...currentVba, isCorrect } as VBA);
      setFeedbackRegistered(true);
    } else {
      alert("Error registering feedback");
    }

    setLoadingFeedback(false);
  };

  const handleSubmit: DOMAttributes<HTMLFormElement>["onSubmit"] = async (
    e: any
  ) => {
    e.preventDefault();

    if (accountData?.creditBalance <= 0 && accountData?.isPremium !== true) {
      alert(
        "You have used your monthly AI credits. Upgrade to Pro and get unlimited access!"
      );
      return;
    }

    const input: string = e.target.formula.value;

    setLoading(true);
    setCopied(false);
    setResponse("");
    setCurrentVba(undefined);
    setFeedbackRegistered(false);

    let res: any;
    if (generate) {
      res = await axios.post(
        "/api/generate-vba",
        {
          vba: {
            id: uuidv4(),
            prompt: input,
            operation: "Generate",
            isCorrect: null,
          },
          uid: accountData?.uid,
          creditBalance: !accountData?.isPremium
            ? accountData?.creditBalance - 1
            : accountData?.creditBalance,
          creditMax: 5,
        },
        {
          headers: {
            // @ts-ignore
            Authorization: `Bearer ${user.accessToken}`,
            ExcelBotToken: EXCEL_BOT_TOKEN,
          },
        }
      );
    } else {
      res = await axios.post(
        "/api/explain-vba",
        {
          vba: {
            id: uuidv4(),
            prompt: input,
            operation: "Explain",
            isCorrect: null,
          },
          uid: accountData?.uid,
          creditBalance: !accountData?.isPremium
            ? accountData?.creditBalance - 1
            : accountData?.creditBalance,
          creditMax: 5,
        },
        {
          headers: {
            // @ts-ignore
            Authorization: `Bearer ${user.accessToken}`,
            ExcelBotToken: EXCEL_BOT_TOKEN,
          },
        }
      );
    }

    if (res.status === 200) {
      window.gtag(
        'event',
        'vba_prompt', {
        'prompt': input
      }
      )
      addVba(res.data);
      setCurrentVba(res.data);
      setResponse(res.data.response);
      if (!accountData?.isPremium) {
        setCreditBalance(accountData?.creditBalance - 1);
      }
    } else {
      alert("Error generating formula - " + JSON.stringify(res.data));
    }

    setLoading(false);
  };

  return (
    <div className="pt-5 pb-10 w-full h-full lg:scrollbar-thin lg:scrollbar-thumb-gray-500 lg:overflow-y-scroll lg:scrollbar-thumb-rounded-full lg:scrollbar-track-rounded-full">
      <h1 className="pl-8 pb-2 text-3xl font-bold border-b-2">
        VBA Playground
      </h1>

      {oldStripeData && <ResubscribeCta />}

      <div className="page-content-wrapper">
        <div className="w-full">
          {!user?.emailVerified ? (
            <EmailVerificationCta />
          ) : (
            <div>
              <form onSubmit={handleSubmit}>
                <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                  Do you want to generate VBA code or have a code explained?
                </p>

                <ul className="grid gap-6 max-w-md md:grid-cols-2">
                  <li>
                    <input
                      checked={generate}
                      onChange={(e) => {
                        setGenerate(!generate);
                        setExplain(!explain);
                      }}
                      type="radio"
                      id="generate"
                      name="generate"
                      value="generate"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="generate"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">Generate</div>
                    </label>
                  </li>
                  <li>
                    <input
                      checked={explain}
                      onChange={(e) => {
                        setGenerate(!generate);
                        setExplain(!explain);
                      }}
                      type="radio"
                      id="explain"
                      name="explain"
                      value="explain"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="explain"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">Explain</div>
                    </label>
                  </li>
                </ul>

                <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                  Type the problem you are trying to solve or code you want
                  explained. Be as specific as possible
                </p>
                <div className="form-control mb-5">
                  <textarea
                    className="textarea textarea-bordered rounded-xl w-3/4 h-24 ml-2"
                    placeholder={placeholder}
                    name="formula"
                    required
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  loading={loading}
                  loadingText={"Running AI..."}
                >
                  Get {generate ? "Code" : "Explanation"}
                </Button>
              </form>

              <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                Output
              </p>

              <textarea
                className="textarea textarea-bordered rounded-xl w-3/4 h-24 ml-2 whitespace-pre-wrap"
                placeholder={output}
                name="output"
                readOnly
                value={response}
                required
              ></textarea>

              {response && (
                <>
                  <div className="mb-4">
                    <CopyToClipboard
                      text={response}
                      onCopy={() => setCopied(true)}
                    >
                      <button
                        type="button"
                        className={`flex items-center gap-2 p-2 mt-3 border rounded-xl ${copied ? "bg-green-300" : "bg-white"
                          }`}
                      >
                        {copied ? (
                          <>
                            <span className="material-icons">done</span>
                            Copied
                          </>
                        ) : (
                          <>
                            <span className="material-icons">content_copy</span>
                            Copy
                          </>
                        )}{" "}
                      </button>
                    </CopyToClipboard>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2 mt-2">
                      Is the output correct?
                    </p>
                    <div className="flex gap-4">
                      <button
                        className={`btn btn-sm btn-square btn-success ${loadingFeedback && "loading"
                          }`}
                        onClick={() => handleFeedback(true)}
                        disabled={feedbackRegistered}
                      >
                        {!loadingFeedback && (
                          <span className="material-icons">thumb_up</span>
                        )}
                      </button>
                      <button
                        className={`btn btn-sm btn-square btn-error ${loadingFeedback && "loading"
                          }`}
                        onClick={() => handleFeedback(false)}
                        disabled={feedbackRegistered}
                      >
                        {!loadingFeedback && (
                          <span className="material-icons">thumb_down</span>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {user?.emailVerified && (
          <div className="mt-8 mobile-margin-addons-block">
            <InstallAddonCta view="vertical" />
          </div>
        )}
      </div>
    </div>
  );
};

export default VbaBuilder;
